/**
 * Job wizard component
 * Multi-step form for creating new scraping jobs
 */

import { useState, useMemo } from 'react';
import { Card, Button, Space } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StepIndicator } from './StepIndicator';
import { QueryStep } from './QueryStep';
import { LocationStep } from './LocationStep';
import { OptionsStep } from './OptionsStep';
import { ReviewStep } from './ReviewStep';
import { useCreateJob } from '@/hooks/useJobs';
import { createJobSchema } from '@/utils/validators';
import type { CreateJobInput } from '@/types/job';
import type { Bounds } from '@/types/grid';

interface JobWizardProps {
  onSuccess?: (jobId: string) => void;
}

type WizardStep = 'query' | 'location' | 'options' | 'review';

const STEPS: { key: WizardStep; title: string; description: string }[] = [
  { key: 'query', title: 'Search Query', description: 'What to search for' },
  { key: 'location', title: 'Location', description: 'Define the search area' },
  { key: 'options', title: 'Options', description: 'Configure settings' },
  { key: 'review', title: 'Review', description: 'Confirm and create' },
];

export function JobWizard({ onSuccess }: JobWizardProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const createJob = useCreateJob();

  const methods = useForm<CreateJobInput>({
    resolver: zodResolver(createJobSchema),
    defaultValues: {
      searchQuery: '',
      name: '',
      bounds: undefined,
      zoomLevel: 16,
      maxResultsPerCell: 120,
      gridOverlapPercent: 10,
      includeDetails: true,
      scrambleOrder: true,
    },
    mode: 'onChange',
  });

  const { handleSubmit, watch, trigger, formState } = methods;
  const formData = watch();

  // Validation for each step
  const stepValidation = useMemo(
    () => ({
      query: !!formData.searchQuery?.trim(),
      location: !!formData.bounds,
      options: true, // Options are optional
      review: true,
    }),
    [formData]
  );

  const currentStepKey = STEPS[currentStep].key;
  const isCurrentStepValid = stepValidation[currentStepKey];
  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = async () => {
    // Validate current step fields
    const fieldsToValidate: (keyof CreateJobInput)[] = {
      query: ['searchQuery'],
      location: ['bounds'],
      options: ['zoomLevel', 'maxResultsPerCell'],
      review: [],
    }[currentStepKey] as (keyof CreateJobInput)[];

    if (fieldsToValidate.length > 0) {
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCancel = () => {
    navigate('/jobs');
  };

  const onSubmit = async (data: CreateJobInput) => {
    try {
      const result = await createJob.mutateAsync(data);
      // Navigation handled here, notification handled by useCreateJob hook
      if (onSuccess) {
        onSuccess(result.id);
      } else {
        navigate(`/jobs/${result.id}`);
      }
    } catch {
      // Error notification handled by useCreateJob hook
    }
  };

  const renderStep = () => {
    switch (currentStepKey) {
      case 'query':
        return <QueryStep />;
      case 'location':
        return <LocationStep />;
      case 'options':
        return <OptionsStep />;
      case 'review':
        return <ReviewStep />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Card
        title="Create New Scraping Job"
        styles={{ body: { padding: '24px 32px' } }}
      >
        {/* Step indicator */}
        <StepIndicator
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={(step) => {
            // Only allow going back to completed steps
            if (step < currentStep) {
              setCurrentStep(step);
            }
          }}
        />

        {/* Step content */}
        <div style={{ minHeight: 400, marginTop: 32, marginBottom: 32 }}>
          {renderStep()}
        </div>

        {/* Navigation buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--color-border)',
            paddingTop: 24,
          }}
        >
          <Button onClick={handleCancel}>Cancel</Button>

          <Space size={12}>
            {!isFirstStep && (
              <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                Back
              </Button>
            )}

            {isLastStep ? (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSubmit(onSubmit)}
                loading={createJob.isPending}
                disabled={!formState.isValid}
              >
                Create Job
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={!isCurrentStepValid}
              >
                Next <ArrowRightOutlined />
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </FormProvider>
  );
}

export default JobWizard;
